/**
 * Scene parameters: everything a permalink has to carry.
 *
 * Every numeric parameter is quantised to one byte in the permalink, so the
 * table below is the contract: order matters, and new parameters go at the
 * end so old links keep decoding. Never reorder or remove a row; retire a row
 * by leaving it in place and ignoring it.
 */

export type Scale = 'lin' | 'log';

export interface ParamSpec {
  /** Key on SceneParams. */
  key: NumericParamKey;
  min: number;
  max: number;
  default: number;
  scale: Scale;
  /** Shown in basic mode, or only in expert mode. */
  basic: boolean;
  /** Slider step for the UI (in real units). */
  step: number;
}

export interface SceneParams {
  /** Catalog or upload id of the background photo. */
  photo: string;
  /** Seed for the initial flock and the predator schedule. */
  seed: number;
  /** Bit flags, see Flags. */
  flags: number;

  count: number;
  cohesion: number;
  separation: number;
  alignment: number;
  speed: number;
  nervousness: number;
  predatorRate: number;
  predatorAggression: number;
  horizon: number;
  horizonTilt: number;
  lightAngle: number;
  lightWarmth: number;
  haze: number;
  birdSize: number;
  flockHeight: number;
  flockDistance: number;
  flockSpread: number;
  turnRate: number;
  flapRate: number;
  crowding: number;
  /** 1: light follows the photo's analysis; 0: lightAngle/lightWarmth are the user's own. */
  lightAuto: number;
}

export type NumericParamKey = Exclude<keyof SceneParams, 'photo' | 'seed' | 'flags'>;

export const Flags = {
  kiosk: 1 << 0,
  hideControls: 1 << 1,
  liveAllowed: 1 << 2,
} as const;

/** Order is the wire format. Append only. */
export const PARAM_TABLE: readonly ParamSpec[] = [
  { key: 'count', min: 500, max: 120000, default: 40000, scale: 'log', basic: true, step: 500 },
  { key: 'cohesion', min: 0, max: 2, default: 0.9, scale: 'lin', basic: true, step: 0.01 },
  { key: 'separation', min: 0, max: 2, default: 1.0, scale: 'lin', basic: true, step: 0.01 },
  { key: 'alignment', min: 0, max: 2, default: 1.0, scale: 'lin', basic: true, step: 0.01 },
  { key: 'speed', min: 6, max: 20, default: 12, scale: 'lin', basic: true, step: 0.1 },
  { key: 'nervousness', min: 0, max: 1, default: 0.4, scale: 'lin', basic: true, step: 0.01 },
  { key: 'predatorRate', min: 0, max: 1, default: 0.3, scale: 'lin', basic: true, step: 0.01 },
  { key: 'predatorAggression', min: 0, max: 1, default: 0.5, scale: 'lin', basic: false, step: 0.01 },
  { key: 'horizon', min: 0, max: 1, default: 0.5, scale: 'lin', basic: false, step: 0.001 },
  { key: 'horizonTilt', min: -10, max: 10, default: 0, scale: 'lin', basic: false, step: 0.1 },
  { key: 'lightAngle', min: 0, max: 360, default: 0, scale: 'lin', basic: false, step: 1 },
  { key: 'lightWarmth', min: 0, max: 1, default: 0.5, scale: 'lin', basic: false, step: 0.01 },
  { key: 'haze', min: 0, max: 1, default: 0.4, scale: 'lin', basic: true, step: 0.01 },
  { key: 'birdSize', min: 0.5, max: 2, default: 1, scale: 'lin', basic: false, step: 0.01 },
  { key: 'flockHeight', min: 0, max: 1, default: 0.4, scale: 'lin', basic: true, step: 0.01 },
  { key: 'flockDistance', min: 0, max: 1, default: 0.5, scale: 'lin', basic: true, step: 0.01 },
  { key: 'flockSpread', min: 0, max: 1, default: 0.5, scale: 'lin', basic: false, step: 0.01 },
  { key: 'turnRate', min: 0.5, max: 4, default: 2, scale: 'lin', basic: false, step: 0.01 },
  { key: 'flapRate', min: 4, max: 16, default: 9, scale: 'lin', basic: false, step: 0.1 },
  { key: 'crowding', min: 0, max: 1, default: 0.5, scale: 'lin', basic: false, step: 0.01 },
  // Appended in M2. Links written before it decode as 1, so their light comes
  // from the photo rather than from bytes that were never a choice.
  { key: 'lightAuto', min: 0, max: 1, default: 1, scale: 'lin', basic: false, step: 1 },
];

export const PARAM_BY_KEY: ReadonlyMap<NumericParamKey, ParamSpec> = new Map(
  PARAM_TABLE.map((p) => [p.key, p]),
);

export const DEFAULT_PHOTO = 'sunset-water';

export function defaultParams(): SceneParams {
  const p = {
    photo: DEFAULT_PHOTO,
    seed: 1,
    flags: Flags.liveAllowed,
  } as SceneParams;
  for (const spec of PARAM_TABLE) p[spec.key] = spec.default;
  return p;
}

export function randomSeed(): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return buf[0]! >>> 0;
}

/** Real value → byte, using the row's scale. */
export function quantise(spec: ParamSpec, value: number): number {
  const v = Math.min(spec.max, Math.max(spec.min, value));
  let t: number;
  if (spec.scale === 'log') {
    t = Math.log(v / spec.min) / Math.log(spec.max / spec.min);
  } else {
    t = (v - spec.min) / (spec.max - spec.min);
  }
  return Math.round(t * 255) & 0xff;
}

/** Byte → real value, the inverse of quantise up to rounding. */
export function dequantise(spec: ParamSpec, byte: number): number {
  const t = (byte & 0xff) / 255;
  if (spec.scale === 'log') {
    return spec.min * Math.pow(spec.max / spec.min, t);
  }
  return spec.min + t * (spec.max - spec.min);
}

/** Snap a value to what the permalink can represent, so the UI never shows a value the link cannot carry. */
export function snap(spec: ParamSpec, value: number): number {
  return dequantise(spec, quantise(spec, value));
}
